/**
 * The release confirmation (f10.9, design D5). Complete: a plain
 * confirmation. Partial: the missing list and a required acknowledgement that
 * gates the confirm button and sends `acknowledge_missing: true`.
 *
 * A `409 CYCLE_RELEASE_INCOMPLETE` refetches readiness (the mutation
 * invalidates the cycle's queries), so the dialog re-renders in its partial
 * form with the fresh list; the acknowledgement resets when the list changes.
 */

import { useEffect, useState } from 'react';
import { Send } from 'lucide-react';

import type { ReleaseReadiness } from '@/api/client';
import { useReleaseCycle } from '@/api/queries';
import { Modal } from '@/routes/reference-data/Modal';

import { partialNotice, releaseFailure } from './releaseView';

export function ReleaseDialog({
  cycleId,
  cycleVersion,
  readiness,
  onClose,
  onReleased,
}: {
  cycleId: string;
  cycleVersion: number;
  readiness: ReleaseReadiness;
  onClose: () => void;
  onReleased?: (notice: string | null) => void;
}) {
  const release = useReleaseCycle(cycleId);
  const [acknowledged, setAcknowledged] = useState(false);
  const partial = readiness.missing.length > 0;
  const missingKey = readiness.missing.join('\n');

  // A fresh missing list must be acknowledged afresh.
  useEffect(() => setAcknowledged(false), [missingKey]);

  const submit = () =>
    release.mutate(
      { expected_version: cycleVersion, acknowledge_missing: partial },
      {
        onSuccess: (result) => {
          onReleased?.(result.partial ? partialNotice(result.missing.length) : null);
          onClose();
        },
      },
    );

  return (
    <Modal
      title={partial ? 'Release with missing data' : `Release cycle ${readiness.cycle_code} to managers?`}
      subtitle={`Cycle ${readiness.cycle_code}`}
      onClose={onClose}
      onSubmit={submit}
      busy={release.isPending}
      submitDisabled={partial && !acknowledged}
      submitLabel="Release to managers"
      submitIcon={<Send aria-hidden="true" />}
      narrow
      layout="plain"
      message={releaseFailure(release.error)}
    >
      <p>Managers can start entering forecasts immediately.</p>
      {partial && (
        <>
          <div className="alert alert-warning mb-4" role="status">
            <div>
              <div className="a-title">
                {readiness.missing.length} item{readiness.missing.length === 1 ? ' is' : 's are'} missing
              </div>
              <ul className="release-missing-list">
                {readiness.missing.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </div>
          </div>
          <label className="check release-ack">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            I understand managers will start with incomplete data
          </label>
        </>
      )}
    </Modal>
  );
}
